import { toast } from 'react-toastify';
import type { GenericList } from './models/JobSharedInterfaces';
import type { UserDetails } from './models/UserInterfaces';
import { useEffect, useState } from 'react';
import { GetQuery, PostQuery } from './Shared';

interface UserDetailsProps {
    id: number;
    userDetails: UserDetails;
    updateText(fieldName: string, fieldValue: string): void;
    updateNumber(fieldName: string, fieldValue: number): void;
    save(): void;
}


const UserDetailData = (props: UserDetailsProps) => {
    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [companies, setCompanies] = useState<GenericList[]>([]);
    const [departments, setDepartments] = useState<GenericList[]>([]);
    const [statusOptions, setStatusOptions] = useState<GenericList[]>([]);
    const [emailSignatures, setEmailSignatures] = useState<GenericList[]>([]);

    useEffect(() => {
        getData();
    }, []);

    const getData = async () => {
        const apiUrl = "GetUserDropdowns";
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setCompanies(result.data.companies);
            setDepartments(result.data.departments);
            setStatusOptions(result.data.statusOptions);
            setEmailSignatures(result.data.emailSignatures);
            setLoading(false);
        };
    }

    const clearErrors = (errorName: string) => {
        let errorList = errors;
        if (errorName) {
            delete errorList[errorName];
            setErrors(errorList);
        }
    }

    const handleChangeText = (e: any) => {
        clearErrors(e.target.name);

        props.updateText(e.target.name, e.target.value);
    }

    const handleChangeNumber = (e: any) => {
        clearErrors(e.target.name);

        props.updateNumber(e.target.name, Number(e.target.value));
    }

    const resetPassword = () => {
        resetUserPassword();
    }

    const resetUserPassword = async () => {
        var details = {
            "username": props.userDetails.userName
        };

        var jsonItem = JSON.stringify(details);

        const apiUrl = "ForgotPassword";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error resetting password!");
        } else {
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                errors.map(function (error: any) {
                    toast.error(error.content);
                });
            } else {
                toast.success("Reset Password Email sent to " + props.userDetails.email);
            }
        };
    }

    const validate = () => {
        let fields = props.userDetails;
        let errorList: any = {};
        let formIsValid = true;

        if (!fields.firstName) {
            formIsValid = false;
            errorList["firstName"] = "First Name is required";
        }
        if (!fields.lastName) {
            formIsValid = false;
            errorList["lastName"] = "Last Name is required";
        }
        if (fields.departmentId < 1) {
            formIsValid = false;
            errorList["departmentId"] = "Department is required";
        }
        if (!fields.userName) {
            formIsValid = false;
            errorList["userName"] = "Username is required";
        }
        if (!fields.email) {
            formIsValid = false;
            errorList["email"] = "Email is required";
        }
        if (props.id == 0) {
            //check password - if existing user, separate form to change password
            if (!fields.password) {
                formIsValid = false;
                errorList["password"] = "Password is required";
            }
            if (!fields.confirmPassword) {
                formIsValid = false;
                errorList["confirmPassword"] = "Confirm Password is required";
            }
            if (fields.password && fields.confirmPassword) {
                //check password and confirm password match
                if (fields.password != fields.confirmPassword) {
                    formIsValid = false;
                    errorList["confirmPassword"] = "Password and Confirm Password must match";
                }
            }
        }
        if (fields.statusId < 0) {
            formIsValid = false;
            errorList["statusId"] = "Status is required";
        }

        setErrors(errorList);
        return formIsValid;
    }

    const save = (e: any) => {
        e.preventDefault();
        if (validate()) {
            props.save();
        }
    }

    const renderDetails = ( 
        <div>
            <label className="input-group" htmlFor="email">
                <span className="label">Email</span>
                <input className="input" type="text" id="email" name="email" value={props.userDetails.email} onChange={(e) => handleChangeText(e)} disabled={props.id == 0 ? false : true}></input>
            </label>
            <span className="label errors">{errors["email"]}</span>

            <label className="input-group" htmlFor="userName">
                <span className="label">Username</span>
                <input className="input" type="text" id="userName" name="userName" value={props.userDetails.userName} onChange={(e) => handleChangeText(e)} disabled={props.id == 0 ? false : true}></input>
            </label>
            <span className="label errors">{errors["userName"]}</span>

            <div className={props.id == 0 ? "" : "hidden"}>
                <label className="input-group" htmlFor="password">
                    <span className="label">Password</span>
                    <input className="input" type="password" id="password" name="password" value={props.userDetails.password} onChange={(e) => handleChangeText(e)}></input>
                </label>
                <span className="label errors">{errors["password"]}</span>
            </div>
            <div className={props.id == 0 ? "" : "hidden"}>
                <label className="input-group" htmlFor="confirmPassword">
                    <span className="label">Confirm Password</span>
                    <input className="input" type="password" id="confirmPassword" name="confirmPassword" value={props.userDetails.confirmPassword} onChange={(e) => handleChangeText(e)}></input>
                </label>
                <span className="label errors">{errors["confirmPassword"]}</span>
            </div>
            <div className={props.id === 0 ? "hidden" : ""}>
                <label className="input-group" htmlFor="companyId">
                    <span className="label">Company</span>
                    <select className="select" id="companyId" name="companyId" value={props.userDetails.companyId} disabled>
                        <option hidden defaultValue="-1"></option>
                        {companies.map(comp =>
                            <option key={comp.id} value={comp.id}>{comp.name}</option>
                        )};
                    </select>
                </label>
                <span className="label errors">{errors["companyId"]}</span>
            </div>

            <label className="input-group" htmlFor="departmentId">
                <span className="label">Department</span>
                <select className="select" id="departmentId" name="departmentId" value={props.userDetails.departmentId} onChange={(e) => handleChangeNumber(e)}>
                    <option hidden defaultValue="-1"></option>
                    {departments.map(dept =>
                        <option key={dept.id} value={dept.id}>{dept.name}</option>
                    )};
                </select>
            </label>
            <span className="label errors">{errors["departmentId"]}</span>

            <label className="input-group" htmlFor="extension">
                <span className="label">Extension</span>
                <input className="input" type="text" id="extension" name="extension" value={props.userDetails.extension} onChange={(e) => handleChangeText(e)}></input>
            </label>

            <label className="input-group" htmlFor="firstName">
                <span className="label">First Name</span>
                <input className="input" type="text" id="firstName" name="firstName" value={props.userDetails.firstName} onChange={(e) => handleChangeText(e)}></input>
            </label>
            <span className="label errors">{errors["firstName"]}</span>

            <label className="input-group" htmlFor="lastName">
                <span className="label">Last Name</span>
                <input className="input" type="text" id="lastName" name="lastName" value={props.userDetails.lastName} onChange={(e) => handleChangeText(e)}></input>
            </label>
            <span className="label errors">{errors["lastName"]}</span>

            <label className="input-group" htmlFor="mobile">
                <span className="label">Mobile</span>
                <input className="input" type="text" id="mobile" name="mobile" value={props.userDetails.mobile} onChange={(e) => handleChangeText(e)}></input>
            </label>

            <label className="input-group" htmlFor="emailSignatureId">
                <span className="label">Email Signature</span>
                <select className="select" id="emailSignatureId" name="emailSignatureId" value={props.userDetails.emailSignatureId} onChange={(e) => handleChangeNumber(e)}>
                    <option hidden defaultValue="-1"></option>
                    {emailSignatures.map(sign =>
                        <option key={sign.id} value={sign.id}>{sign.name}</option>
                    )};
                </select>
            </label>

            <label className="input-group" htmlFor="statusId">
                <span className="label">Status</span>
                <select className="select" id="statusId" name="statusId" value={props.userDetails.statusId} onChange={(e) => handleChangeNumber(e)} disabled={props.userDetails.id == 0 ? true : false}>
                    {statusOptions.map(status =>
                        <option key={status.id} value={status.id}>{status.name}</option>
                    )};
                </select>
            </label>
            <span className="label errors">{errors["statusId"]}</span>

            <button className="defaultbutton defaultbutton-prev" type="button" onClick={save}>Save</button>
            <button className={props.userDetails.id === 0 ? "hidden" : "defaultbutton defaultbutton-next"} type="button" onClick={resetPassword}>Reset Password</button>

        </div>
            
    );
    
    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;


    return (<div>
        {contents}
    </div>);

}

export default UserDetailData;